/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/MultiFieldPage';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { getCustomChildItemTypes } from '../../registry/Manager';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './MultiField';

const MultiFieldManager: ComponentManagerFn<'MultiField'> = (
  platform: Platform,
  type: 'MultiField',
): ComponentManager<'MultiField'> => {
  return {
    ...baseComponentManager<'MultiField'>(platform, 'MultiField'),
    type: 'MultiField',
    isChildAllowed: (_page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('Form'),
          'Checkbox',
          'DateField',
          'IconSelect',
          'LOVField',
          'LookupSelect',
          'NumberField',
          'RadioBox',
          'Select',
          'Icon',
          'IconSwitch',
          'TextField',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'insertable':
        case 'updatable':
        case 'viewAttribute':
        case 'label':
        case 'helperText':
        case 'required':
        case 'labelColor':
        case 'bgColorExpression':
        case 'colorExpression':
          return false;
        default:
          return true;
      }
    },
    name: 'Multi Field',
    category: 'Forms',
    icon: { iconName: 'io-text-input', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Multi Field component is a layout utility designed to group multiple form inputs or visual elements tightly together into a single "logical" field. Unlike a standard container, it visually merges its children, often removing the spacing between them to create composite inputs.
Use this component when you need to combine related elements—like an input box and an action button, or a text field and a status icon—so they appear and behave as a single unit on the form.

**1. Grouping Capability**<br/>
The Multi Field accepts a wide variety of form components as children, allowing for flexible combinations.
* **Supported Inputs:** TextField, NumberField, DateField, Select, Checkbox, RadioBox, LOVField, LookupSelect.
* **Visual Elements:** Icon, IconSwitch.

**2. Configuration & Layout**<br/>
This component is purely a wrapper; it delegates data handling to its children.
* **Properties Hidden:** Standard field properties like **Label**, **View Attribute**, **Required**, and **Helper Text** are explicitly hidden. This is because the Multi Field itself doesn't hold data; its **children** do.
* **Flex Layout:** You can control the width of the entire group using the **flex** property, allowing the composite field to span part or all of the form row.

**3. Best Use Cases**<br/>
* **Input with Status:** Grouping a **TextField** (for a User ID) with an **IconSwitch** (Green Check/Red X) to visually indicate if the ID is available.
* **Search Bar:** Grouping a **TextField** with a "Search" **Icon** button.
* **Unit of Measure:** Grouping a **NumberField** (Amount) with a read-only **TextField** or label (e.g., "USD") so they sit side-by-side.
* **Date Range:** Grouping two **DateField** components ("Start" and "End") to appear as a single logical row.`,
    helpRenders: [
      {
        title: 'Default Multi Field',
        description:
          'In the below example, Multi Fild is used to render a Text Field along with an Icon',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: false,
        hideProps: false,
        height: 300,
        visibleProps: ['flex'],
        item: {
          children: ['textField', 'icon'],
          itemId: 'multiField',
          parent: 'form',
          props: {
            flex: {
              xs: 12,
            },
          },
          type: 'MultiField',
        },
      },
    ],
  };
};

export default MultiFieldManager;
